
import React, { useRef, useEffect, useState } from 'react';
import { regions_mexico } from '../data/regions';
import { fetchJoshuaProjectData } from '../data/utils/joshua-project-api';
import { useRegionCityState } from '../hooks/use-region-city-state';
import { useFilterState } from '../hooks/use-filter-state';
import { useUrlParams } from '../hooks/use-url-params';
import { MexicoMapRef } from '../components/map';
import { ChurchPresence } from '../data/types/region-types';
import FilterPanel from '../components/filters/FilterPanel';
import Header from '../components/layout/Header';
import MainLayout from '../components/layout/MainLayout';
import MapContainer from '../components/map-view/MapContainer';
import Sidebars from '../components/map-view/Sidebars';
import { LegendFilterProvider } from '../contexts/legend-filter/LegendFilterContext';
import { useToast } from '@/hooks/use-toast';

const Index = () => {
  const mapRef = useRef<MexicoMapRef>(null);
  const { toast } = useToast();
  
  // Pin visibility toggles
  const [showFunding, setShowFunding] = useState(true);
  const [showLowPresence, setShowLowPresence] = useState(true);
  const [showUnreached, setShowUnreached] = useState(true);
  const [showAreasOfNeed, setShowAreasOfNeed] = useState(true);
  
  const {
    selectedRegion,
    selectedCity,
    isChiapasActive,
    isHidalgoActive,
    wasInMexicoView,
    handleRegionClick,
    handleCityClick,
    handleRegionSelect,
    handleCitySelect,
    handleGoBack
  } = useRegionCityState();

  const {
    searchTerm,
    setSearchTerm,
    churchPresenceFilter,
    setChurchPresenceFilter,
    fundingFilter,
    setFundingFilter,
    filteredRegions,
    showFilters,
    setShowFilters,
    resetFilters
  } = useFilterState();

  const { handleInitialParams } = useUrlParams(handleRegionSelect, mapRef);

  useEffect(() => {
    handleInitialParams();
    
    // Load global pledge data on app initialization and show global view by default
    const loadGlobalData = async () => {
      try {
        const globalData = await fetchJoshuaProjectData();
        console.log('Global data loaded for initial application state:', globalData.length, 'regions');
        
        // Show a toast to inform users they're seeing the global view
        toast({
          title: "Global View",
          description: "Showing pledges across regions worldwide",
          duration: 3000,
        });
      } catch (error) {
        console.error('Error loading global data:', error);
      }
    };
    
    loadGlobalData();
    
    // Ensure we're in global view on initial load
    if (mapRef.current) {
      mapRef.current.resetView();
    }
  }, []);

  // Apply visibility classes to body based on toggle states
  useEffect(() => {
    const body = document.body;
    
    if (!showFunding) {
      body.classList.add('hide-funding');
    } else {
      body.classList.remove('hide-funding');
    }
    
    if (!showLowPresence) {
      body.classList.add('hide-low');
    } else {
      body.classList.remove('hide-low');
    }
    
    if (!showUnreached) {
      body.classList.add('hide-unreached');
    } else {
      body.classList.remove('hide-unreached');
    }
    
    if (!showAreasOfNeed) {
      body.classList.add('hide-need');
    } else {
      body.classList.remove('hide-need');
    }
    
    return () => {
      // Clean up classes on unmount
      body.classList.remove('hide-funding', 'hide-low', 'hide-unreached', 'hide-need');
    };
  }, [showFunding, showLowPresence, showUnreached, showAreasOfNeed]);

  const handleRegionIdSelect = (regionId: string) => {
    const region = regions_mexico.find(r => r.region_id === regionId);
    if (region) {
      handleRegionSelect(region);
    }
  };

  const handleHomeClick = () => {
    if (mapRef.current) {
      console.log("World View button clicked, showing global view");
      
      // Use the resetView method to show global view
      mapRef.current.resetView();
      
      // Show a toast to confirm the global view is being displayed
      toast({
        title: "Global View",
        description: "Showing pledges across regions worldwide",
        duration: 3000,
      });
      
      // Clear any selected region or city
      handleRegionSelect(null);
    }
  };
  
  const handleMexicoClick = () => {
    if (mapRef.current) {
      console.log("Mexico button clicked, focusing on Mexico");
      
      // Use the new showMexicoView method
      mapRef.current.showMexicoView();
      
      // Clear any selected region
      handleRegionSelect(null);
      
      // Show a toast to confirm we're showing Mexico
      toast({
        title: "Mexico",
        description: "Showing Mexican regions and pledges",
        duration: 3000,
      });
    }
  };

  return (
    <LegendFilterProvider>
      <MainLayout isChiapasActive={isChiapasActive} isHidalgoActive={isHidalgoActive}>
        <Header 
          onPledgeClick={() => {}}
          onPitchClick={() => {}}
          regions={regions_mexico}
          onRegionSelect={handleRegionIdSelect}
          onCitySelect={handleCitySelect}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          isChiapasActive={isChiapasActive}
          isHidalgoActive={isHidalgoActive}
          onHomeClick={handleHomeClick}
          onMexicoClick={handleMexicoClick}
        />
        
        <MapContainer 
          regions={regions_mexico}
          onRegionClick={handleRegionClick}
          filteredRegionIds={filteredRegions.map(r => r.region_id)}
          selectedRegion={selectedRegion}
          selectedCity={selectedCity}
          wasInMexicoView={wasInMexicoView}
          onCityClick={handleCityClick}
          handleGoBack={handleGoBack}
          isChiapasActive={isChiapasActive}
          isHidalgoActive={isHidalgoActive}
          mapRef={mapRef}
          showFunding={showFunding}
          showLowPresence={showLowPresence}
          showUnreached={showUnreached}
          showAreasOfNeed={showAreasOfNeed}
        />
        
        <FilterPanel 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          churchPresenceFilter={churchPresenceFilter}
          setChurchPresenceFilter={setChurchPresenceFilter}
          fundingFilter={fundingFilter}
          setFundingFilter={setFundingFilter}
          resetFilters={resetFilters}
          regions={regions_mexico}
          onRegionSelect={handleRegionSelect}
          onCitySelect={handleCitySelect}
          totalPledges={0}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          isChiapasActive={isChiapasActive}
          isHidalgoActive={isHidalgoActive}
          showFunding={showFunding}
          setShowFunding={setShowFunding}
          showLowPresence={showLowPresence}
          setShowLowPresence={setShowLowPresence}
          showUnreached={showUnreached}
          setShowUnreached={setShowUnreached}
          showAreasOfNeed={showAreasOfNeed}
          setShowAreasOfNeed={setShowAreasOfNeed}
        />

        <Sidebars 
          selectedRegion={selectedRegion}
          selectedCity={selectedCity}
          handleGoBack={handleGoBack}
          handleCitySelect={handleCitySelect}
        />
      </MainLayout>
    </LegendFilterProvider>
  );
};

export default Index;
